import { useState, useRef, useEffect } from "react";
import { getRawInitData, getTelegramUser } from "../utils/telegram";

/* ---------------------------------------------------------------
   AUTH — on app load, forward the raw Telegram WebApp init-data
   string to the backend as the X-Telegram-Init-Data header. The
   backend is responsible for verifying the Telegram HMAC signature,
   extracting telegram_id, issuing a JWT, and looking the user up:
     { status: "ok",        token, user }        -> known account
     { status: "not_found", token }               -> show registration
   The "not_found" token is a short-lived registration token bound
   to the same telegram_id (no account_id claim yet) — it's the only
   thing allowed to call /auth/link-account below.

   BROWSER (non-Telegram) FALLBACK — when the app is opened outside
   the Telegram WebApp container, getRawInitData() comes back empty.
   In that case we skip the Telegram handshake entirely and instead:
     - if a token pair is already stored from a previous browser
       session, try to restore it via GET user/me;
     - otherwise show BrowserAuthScreen, which posts to
       user/auth/login (email + password) or user/auth/signup
       (email + password + Pocket account id) and gets back the same
       { status: "ok", token, refresh_token, user, stats } shape as
       the Telegram flow, so everything downstream (authFetch,
       refresh, logout) is shared code.
------------------------------------------------------------------ */
export const BASE_URL = "https://market-maker.pro/api/";
const JWT_STORAGE_KEY = "pj_jwt";
const REFRESH_STORAGE_KEY = "pj_refresh";

// Same host as BASE_URL. Only checked when a brand-new Telegram user
// (auth -> status: "not_found") is about to be shown the registration
// form, so admins can flip registration off remotely without a deploy.
const REG_SECTION_URL = BASE_URL + "user/reg-section";

/* Persist / read / clear the (access, refresh) pair together so they
   never drift out of sync in localStorage. */
function storeTokens({ token, refresh_token }) {
  try {
    if (token) localStorage.setItem(JWT_STORAGE_KEY, token);
    if (refresh_token) localStorage.setItem(REFRESH_STORAGE_KEY, refresh_token);
  } catch (e) {}
}

function readStoredTokens() {
  try {
    return {
      token: localStorage.getItem(JWT_STORAGE_KEY) || null,
      refreshToken: localStorage.getItem(REFRESH_STORAGE_KEY) || null,
    };
  } catch (e) {
    return { token: null, refreshToken: null };
  }
}

function clearStoredTokens() {
  try {
    localStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch (e) {}
}

/* Some backend endpoints reply with a plain { "detail": "..." } body
   instead of throwing an HTTP-level error — most notably a blocked
   account, which still comes back as a normal JSON response (often
   with a non-2xx status, but not guaranteed). Centralized here so
   every call site checks the same way. */
function isBlockedAccountBody(data) {
  return typeof data?.detail === "string" && data.detail.toLowerCase() === "account is blocked";
}

async function authenticate(signal) {
  const initData = getRawInitData();
  const res = await fetch(BASE_URL + "user/auth", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Telegram-Init-Data": initData,
    },
    signal,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    // fall through — non-JSON body handled below via !res.ok
  }
  if (isBlockedAccountBody(data)) {
    const err = new Error("blocked");
    err.blocked = true;
    throw err;
  }
  if (!res.ok) throw new Error(`auth API ${res.status}`);
  return data; // { status, token, refresh_token?, user? }
}

async function linkAccount(token, accountId, userName, fullName, signal) {
  const res = await fetch(BASE_URL + "user/register", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ account_id: accountId, username: userName, fullname: fullName }),
    signal,
  });
  if (!res.ok) {
    if (res.status === 404) throw new Error("not_found");
    throw new Error(`link-account API ${res.status}`);
  }
  return res.json(); // { status: "ok", token, refresh_token, user }
}

/* Alternate path into the app during the same "register" phase —
   activates a free trial with no code/OTP involved. POST /user/trial
   is unauthenticated on the backend now (it just creates the account
   outright, keyed on telegram_id or email) and the backend meters
   usage itself (5 signals per timeframe — see sectionAccess.js).
   Returns the same { status: "ok", token, refresh_token, user } shape
   as registration, so everything downstream is identical regardless
   of which path was used. */
async function activateTrial({ isTelegram, email, password, telegramId, telegramUsername, telegramFullName, signal }) {
  const body = isTelegram
    ? {
        telegram_id: telegramId,
        telegram_username: telegramUsername,
        telegram_full_name: telegramFullName,
      }
    : {
        email,
        password,
      };

  const res = await fetch(BASE_URL + "user/trial", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  const data = await parseJsonSafe(res);
  if (isBlockedAccountBody(data)) throw browserAuthError("blocked");
  if (res.status === 409) throw browserAuthError("trial_taken");
  if (!res.ok) throw browserAuthError(getAuthValidationError(data));
  return data; // same shape as registration: { status: "ok", token, refresh_token, user, ... }
}

/* --- Browser (email/password) auth --- these mirror authenticate()/
   linkAccount() above but for the non-Telegram path. Error shapes are
   normalized to short codes ("not_found" | "login_taken" |
   "account_id_not_found" | "generic") that BrowserAuthScreen already
   knows how to render, via .code on the thrown Error. */

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch (e) {
    return null;
  }
}

function browserAuthError(code, message) {
  const err = new Error(message || code);
  err.code = code;
  return err;
}

function getApiErrorCode(data) {
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.filter(Boolean).join(" ");
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) return data.detail.filter(Boolean).join(" ");

  const fieldMessage = Object.values(data || {})
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .find((value) => typeof value === "string" && value.trim());
  return fieldMessage || "";
}

function getAuthValidationError(data) {
  const message = getApiErrorCode(data).toLowerCase();
  if (
    (message.includes("password") && (message.includes("8") || message.includes("characters"))) ||
    message.includes("at least 8 characters")
  ) {
    return "password_short";
  }
  return "generic";
}

async function loginWithEmail(email, password, signal) {
  const res = await fetch(BASE_URL + "user/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
    signal,
  });
  const data = await parseJsonSafe(res);
  if (isBlockedAccountBody(data)) throw browserAuthError("blocked");
  if (res.status === 401 || res.status === 404) throw browserAuthError("not_found");
  if (!res.ok) throw browserAuthError("generic", `login API ${res.status}`);
  return data; // { status: "ok", token, refresh_token, user, stats }
}

async function signupWithEmail(email, password, accountId, fullName, signal) {
  const res = await fetch(BASE_URL + "user/auth/signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, account_id: accountId, fullname: fullName }),
    signal,
  });
  const data = await parseJsonSafe(res);
  if (res.status === 409) throw browserAuthError("login_taken");
  if (res.status === 404) throw browserAuthError("account_id_not_found");
  if (!res.ok) throw browserAuthError(getAuthValidationError(data));
  return data; // { status: "ok", token, refresh_token, user }
}

/* Restores a browser session from a previously stored access token —
   the equivalent of authenticate() for users who don't have a fresh
   Telegram init-data string to re-verify on every load. */
async function fetchMe(token, signal) {
  const res = await fetch(BASE_URL + "user/me", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    signal,
  });
  const data = await parseJsonSafe(res);
  if (isBlockedAccountBody(data)) throw browserAuthError("blocked");
  if (!res.ok) throw browserAuthError("generic", `me API ${res.status}`);
  return data; // { status: "ok", user, stats }
}

/* Exchanges a still-valid refresh token for a brand new access+refresh
   pair. Throws on failure (expired/invalid refresh token, account
   unlinked, etc.) so the caller can fall back to a full re-auth. */
async function refreshSession(refreshToken, signal) {
  const res = await fetch(BASE_URL + "user/refresh", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${refreshToken}`,
    },
    signal,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {}
  if (isBlockedAccountBody(data)) {
    const err = new Error("blocked");
    err.blocked = true;
    throw err;
  }
  if (!res.ok) throw new Error(`refresh API ${res.status}`);
  return data; // { status: "ok", token, refresh_token }
}

/* Checks whether registration is currently open. Fails OPEN (returns
   true) on any network/parsing error or non-2xx response, so a
   transient hiccup reaching the admin host never locks brand-new
   users out — the endpoint is only meant to block deliberately, via
   an explicit { is_active: false }. */
async function fetchRegistrationStatus(signal) {
  try {
    const res = await fetch(REG_SECTION_URL, {
      method: "GET",
      signal,
    });
    if (!res.ok) {
      return {
        registrationOpen: true,
        maintenanceMode: false,
        maintenanceEndsAt: null,
        maintenanceDurationSeconds: 3600,
      };
    }
    const data = await res.json();
    const maintenanceMode = data?.maintenance_mode === true;
    return {
      registrationOpen: data?.is_active !== false && !maintenanceMode,
      maintenanceMode,
      maintenanceEndsAt: data?.maintenance_ends_at || null,
      maintenanceDurationSeconds: Number(data?.maintenance_duration_seconds) || 3600,
    };
  } catch (e) {
    if (e.name === "AbortError") throw e;
    return {
      registrationOpen: true,
      maintenanceMode: false,
      maintenanceEndsAt: null,
      maintenanceDurationSeconds: 3600,
    };
  }
}

/* Drives the whole auth lifecycle: loading -> ready | register.
   register -> linking -> ready | register (with error) on failure.
   Both the access token and the refresh token are persisted to
   localStorage, since every other authenticated endpoint needs the
   access token, and the refresh token is what lets a returning session
   get a new access token without re-typing the account ID once the old
   one expires. The backend still re-checks the Telegram signature on
   every fresh app load (see authenticate() above) — the refresh token
   is only used mid-session, when a call comes back 401 because the
   short-lived access token expired. */
export function useAuth() {
  const [state, setState] = useState("loading"); // loading | register | register_closed | linking | ready | blocked | fatal | browser_auth
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState([]);
  const [error, setError] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [registrationChecked, setRegistrationChecked] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceEndsAt, setMaintenanceEndsAt] = useState(null);
  const [maintenanceDurationSeconds, setMaintenanceDurationSeconds] = useState(3600);
  // Distinct from `state` — BrowserAuthScreen needs a plain busy flag
  // while login/signup is in flight, without the screen itself
  // unmounting the way it would if we routed busy through `state`.
  const [authBusy, setAuthBusy] = useState(false);
  // Drives the trial-renewal flow shown from LockedScreen once a trial's
  // signal quota is exhausted — separate from `state`/`error` above since
  // it can be triggered mid-session, on top of an already "ready" app.
  const [trialRenewalStatus, setTrialRenewalStatus] = useState("idle"); // idle | submitting | success | error
  const [trialRenewalError, setTrialRenewalError] = useState(null);

  // Refs mirror the latest tokens so authFetch always reads the current
  // value even inside closures created on an earlier render.
  const tokenRef = useRef(null);
  const refreshTokenRef = useRef(null);
  // Avoids two concurrent 401s each kicking off their own refresh call.
  const refreshInFlightRef = useRef(null);

  const applyTokens = (data) => {
    tokenRef.current = data.token;
    if (data.refresh_token) refreshTokenRef.current = data.refresh_token;
    storeTokens({ token: data.token, refresh_token: data.refresh_token });
    setToken(data.token);
  };

  const logout = () => {
    clearStoredTokens();
    tokenRef.current = null;
    refreshTokenRef.current = null;
    setToken(null);
    setUser(null);
    // Telegram sessions can silently re-authenticate against fresh
    // init-data (handled by the bootstrap effect on next load), but a
    // browser session has nothing to fall back on — send it to the
    // login form instead of the Telegram-only registration screen.
    setState(getRawInitData() ? "register" : "browser_auth");
  };

  /* Exchanges the stored refresh token for a new pair. Deduplicated so
     multiple simultaneous 401s trigger only one network call. */
  const doRefresh = () => {
    if (!refreshInFlightRef.current) {
      refreshInFlightRef.current = (async () => {
        if (!refreshTokenRef.current) throw new Error("no refresh token");
        const data = await refreshSession(refreshTokenRef.current);
        applyTokens(data);
        return data.token;
      })().finally(() => {
        refreshInFlightRef.current = null;
      });
    }
    return refreshInFlightRef.current;
  };

  /* Wrapper for calling any other authenticated backend endpoint.
     Attaches the current access token, and if the response is a 401
     (expired access token) transparently refreshes once and retries. */
  const authFetch = async (path, options = {}) => {
    const doFetch = (bearer) =>
      fetch(BASE_URL + path, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers || {}),
          Authorization: `Bearer ${bearer}`,
        },
      });

    let res = await doFetch(tokenRef.current);
    if (res.status === 401 && refreshTokenRef.current) {
      try {
        const newToken = await doRefresh();
        res = await doFetch(newToken);
      } catch (e) {
        if (e.blocked) {
          setState("blocked");
        } else {
          logout();
        }
        throw e;
      }
    }
    return res;
  };

  const showBrowserAuth = async (signal) => {
    const registrationStatus = await fetchRegistrationStatus(signal);
    setRegistrationOpen(registrationStatus.registrationOpen);
    setMaintenanceMode(registrationStatus.maintenanceMode);
    setMaintenanceEndsAt(registrationStatus.maintenanceEndsAt);
    setMaintenanceDurationSeconds(registrationStatus.maintenanceDurationSeconds);
    setState("browser_auth");
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchRegistrationStatus(controller.signal)
      .then((registrationStatus) => {
        setRegistrationOpen(registrationStatus.registrationOpen);
        setMaintenanceMode(registrationStatus.maintenanceMode);
        setMaintenanceEndsAt(registrationStatus.maintenanceEndsAt);
        setMaintenanceDurationSeconds(registrationStatus.maintenanceDurationSeconds);
        setRegistrationChecked(true);
      })
      .catch((err) => {
        if (err.name !== "AbortError") setRegistrationChecked(true);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const initData = getRawInitData();

    if (!initData) {
      // No Telegram WebApp context — this is a plain browser visit.
      // Try to restore a previously stored browser session; otherwise
      // go straight to the login/signup screen. We never fall back to
      // the Telegram flow from here (there's no init-data to send).
      const stored = readStoredTokens();
      if (!stored.token) {
        showBrowserAuth(controller.signal).catch((err) => {
          if (err.name !== "AbortError") setState("browser_auth");
        });
        return () => controller.abort();
      }
      tokenRef.current = stored.token;
      refreshTokenRef.current = stored.refreshToken;
      setToken(stored.token);
      fetchMe(stored.token, controller.signal)
        .then((data) => {
          if (data.status === "ok" && data.user) {
            setUser(data.user);
            if (Array.isArray(data.stats)) setStats(data.stats);
            setState("ready");
          } else {
            clearStoredTokens();
            return showBrowserAuth(controller.signal);
          }
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          if (err.code === "blocked") {
            setState("blocked");
            return;
          }
          // Stored access token expired/invalid — try the refresh
          // token once before giving up and asking to log in again.
          if (stored.refreshToken) {
            refreshSession(stored.refreshToken, controller.signal)
              .then((refreshed) => {
                applyTokens(refreshed);
                return fetchMe(refreshed.token, controller.signal);
              })
              .then((data) => {
                setUser(data.user);
                if (Array.isArray(data.stats)) setStats(data.stats);
                setState("ready");
              })
              .catch((refreshErr) => {
                if (refreshErr.name === "AbortError") return;
                clearStoredTokens();
                return showBrowserAuth(controller.signal);
              });
          } else {
            clearStoredTokens();
            showBrowserAuth(controller.signal).catch((err) => {
              if (err.name !== "AbortError") setState("browser_auth");
            });
          }
        });
      return () => { controller.abort(); };
    }

    authenticate(controller.signal)
      .then(async (data) => {
        if (data.status === "ok" && data.user) {
          applyTokens(data);
          setUser(data.user);
          if (Array.isArray(data.stats)) setStats(data.stats);
          setState("ready");
        } else {
          // register-scoped token only: keep it in memory (not persisted)
          // until the account gets linked below.
          tokenRef.current = data.token;
          setToken(data.token);
          // Brand-new user (not found in our DB) — check with the admin
          // panel whether registration is currently open before showing
          // the form.
          const registrationStatus = await fetchRegistrationStatus(controller.signal);
          setRegistrationOpen(registrationStatus.registrationOpen);
          setMaintenanceMode(registrationStatus.maintenanceMode);
          setMaintenanceEndsAt(registrationStatus.maintenanceEndsAt);
          setMaintenanceDurationSeconds(registrationStatus.maintenanceDurationSeconds);
          setState(registrationStatus.registrationOpen ? "register" : "register_closed");
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") return; // superseded by a newer effect run
        if (err.blocked) {
          setState("blocked");
          return;
        }
        setState("fatal");
      });
    return () => { controller.abort(); };
  }, []);

  /* Browser login: POST email/password -> same token/user shape as
     the Telegram flow, so everything after this (authFetch, refresh,
     logout) is shared. */
  const loginEmail = async (email, password) => {
    setError(null);
    setAuthBusy(true);
    try {
      const data = await loginWithEmail(email, password);
      applyTokens(data);
      setUser(data.user);
      if (Array.isArray(data.stats)) setStats(data.stats);
      setState("ready");
    } catch (err) {
      if (err.code === "blocked") {
        setState("blocked");
      } else {
        setError(err.code || "generic");
      }
    } finally {
      setAuthBusy(false);
    }
  };

  /* Browser signup: email + password + Pocket account id in one step
     (there's no separate "register-scoped token" phase like Telegram,
     since there's no prior verified identity to attach the account to
     yet — the password itself is the proof of identity going forward). */
  const signupEmail = async (email, password, accountId, fullName) => {
    setError(null);
    setAuthBusy(true);
    try {
      const data = await signupWithEmail(email, password, accountId, fullName);
      applyTokens(data);
      setUser(data.user);
      if (Array.isArray(data.stats)) setStats(data.stats);
      setState("ready");
    } catch (err) {
      setError(err.code === "generic" ? err.message : err.code || err.message || "generic");
    } finally {
      setAuthBusy(false);
    }
  };

  const submitAccountId = async (accountId) => {
    if (!tokenRef.current) return;
    setError(null);
    setState("linking");
    try {
      const tgUser = getTelegramUser();
      const userName = tgUser?.username || "";
      const fullName = [tgUser?.first_name, tgUser?.last_name].filter(Boolean).join(" ");
      const data = await linkAccount(tokenRef.current, accountId, userName, fullName);
      applyTokens(data);
      setUser(data.user);
      setState("ready");
    } catch (err) {
      setError(err.message === "not_found" ? "not_found" : "generic");
      setState("register");
    }
  };

  /* Shown once a trial account has used up its metered signal quota
     (see access/sectionAccess.js -> lockReason "trial_expired"). The
     user creates a fresh Pocket Option account through our referral
     link (same instructions as SwitchAccountScreen during initial
     registration) and submits its ID here to extend/renew access.
     TODO: backend endpoint is not implemented yet — "user/trial-renew"
     is a placeholder path, update it once the real one exists. Unlike
     submitAccountId (register-scoped token), the user is already fully
     authenticated at this point, so this goes through authFetch. */
  const submitTrialRenewal = async (accountId) => {
    setTrialRenewalStatus("submitting");
    setTrialRenewalError(null);
    try {
      const res = await authFetch("user/trial-renew", {
        method: "POST",
        body: JSON.stringify({ account_id: accountId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTrialRenewalStatus("error");
        setTrialRenewalError(data?.detail || `trial-renew API ${res.status}`);
        return;
      }
      if (data?.user) setUser(data.user);
      setTrialRenewalStatus("success");
    } catch (e) {
      setTrialRenewalStatus("error");
      setTrialRenewalError(e?.message || "generic");
    }
  };

  const resetTrialRenewal = () => {
    setTrialRenewalStatus("idle");
    setTrialRenewalError(null);
  };

  /* Same "register" phase as submitAccountId, but grants a free trial
     with no code — email/password for browser sessions, nothing at all
     (beyond the already-verified Telegram identity) for Telegram ones.
     POST /user/trial is unauthenticated on the backend, so there's no
     register-scoped token to attach here the way linkAccount() does. */
  const submitTrial = async (email = "", password = "") => {
    setError(null);
    setAuthBusy(true);
    try {
      const isTelegram = Boolean(getRawInitData());
      const tgUser = isTelegram ? getTelegramUser() : null;
      const telegramUsername = tgUser?.username || "";
      const telegramFullName = [tgUser?.first_name, tgUser?.last_name].filter(Boolean).join(" ");

      const data = await activateTrial({
        isTelegram,
        email,
        password,
        telegramId: tgUser?.id || null,
        telegramUsername,
        telegramFullName,
      });
      applyTokens(data);
      setUser(data.user);
      if (Array.isArray(data.stats)) setStats(data.stats);
      setState("ready");
    } catch (err) {
      if (err.code === "blocked") {
        setState("blocked");
      } else {
        setError(err.code === "generic" ? err.message : err.code || err.message || "generic");
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const refetchInFlightRef = useRef(null);
  // Lightweight refresh of the already-authenticated user's data
  // (e.g. when the Profile tab is opened). Deliberately calls
  // GET user/me via authFetch — NOT authenticate() — since a full
  // Telegram init-data handshake (POST user/auth) has no reason to
  // run again mid-session, and would also silently misbehave for
  // browser sessions where there's no init-data to send.
  const refetchUser = () => {
    if (refetchInFlightRef.current) return refetchInFlightRef.current;
    refetchInFlightRef.current = authFetch("user/me", { method: "GET" })
      .then(async (res) => {
        const data = await parseJsonSafe(res);
        if (isBlockedAccountBody(data)) {
          setState("blocked");
          return;
        }
        if (!res.ok) return; // non-fatal — keep whatever we already have
        if (data?.user) setUser(data.user);
        if (Array.isArray(data?.stats)) setStats(data.stats);
      })
      .catch((err) => {
        if (err?.blocked) {
          setState("blocked");
          return;
        }
        // non-fatal — keep whatever we already have
      })
      .finally(() => {
        refetchInFlightRef.current = null;
      });
    return refetchInFlightRef.current;
  };

  const updateLanguage = async (language) => {
    try {
      const res = await authFetch("user/profile", {
        method: "PATCH",
        body: JSON.stringify({ language }),
      });
      if (!res.ok) throw new Error(`profile API ${res.status}`);
      const data = await res.json();
      if (data.user) setUser(data.user);
    } catch (e) {
      // non-fatal — UI already reflects the choice locally, just didn't
      // persist server-side this time
    }
  };

  /* Request OTP to link a Telegram account via GET /api/user/link-telegram */
  const requestTelegramLinkCode = async () => {
    const res = await authFetch("user/link-telegram", { method: "GET" });
    if (!res.ok) throw new Error(`telegram link API ${res.status}`);
    const data = await res.json();
    const otp = data?.otp ?? data?.code ?? data?.link_code ?? (typeof data === "string" ? data : "");
    return { otp, code: otp };
  };

  /* Link email to the account via POST /api/user/link-email */
  const linkEmail = async (email, password) => {
    const res = await authFetch("user/link-email", {
      method: "POST",
      body: JSON.stringify({ email, password: password || undefined }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const emailErr = Array.isArray(data?.email) ? data.email[0] : null;
      const msg = data?.detail || emailErr || `link email API ${res.status}`;
      const err = new Error(msg);
      // DRF returns email-taken (and any other email-field validation
      // failure, e.g. bad format) as a field-level error under "email".
      // Flag it so the UI can show a targeted message instead of the
      // generic fallback.
      if (emailErr) err.code = "email_invalid";
      throw err;
    }
    const data = await res.json().catch(() => ({}));
    if (data?.user) {
      setUser(data.user);
    } else {
      setUser((u) => (u ? { ...u, email } : u));
    }
    return data;
  };

  /* Set or change the account password */
  const setPassword = async (currentPw, newPw) => {
    const res = await authFetch("user/password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPw || undefined,
        new_password: newPw,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const isWrongPw =
        data?.detail === "wrong_password" ||
        (Array.isArray(data?.detail) && data.detail.includes("wrong_password")) ||
        Boolean(data?.current_password);

      const err = new Error(isWrongPw ? "wrong_password" : data?.detail || `password API ${res.status}`);
      err.detail = isWrongPw ? "wrong_password" : "generic";
      throw err;
    }

    const data = await res.json().catch(() => ({}));
    if (data?.user) {
      setUser(data.user);
    } else {
      setUser((u) => (u ? { ...u, has_password: true } : u));
    }
    return data;
  };

  return {
    state,
    token,
    user,
    stats,
    error,
    authBusy,
    registrationOpen,
    registrationChecked,
    maintenanceMode,
    maintenanceEndsAt,
    maintenanceDurationSeconds,
    submitAccountId,
    submitTrial,
    authFetch,
    updateLanguage,
    refetchUser,
    logout,
    loginEmail,
    signupEmail,
    requestTelegramLinkCode,
    setPassword,
    linkEmail,
    trialRenewalStatus,
    trialRenewalError,
    submitTrialRenewal,
    resetTrialRenewal,
  };
}
